/**
 * Gated HITL bridge for `apply_symmetric_margin` (the mutating path).
 *
 * Flow: parse the model's tool input → `resolveSymmetricMargin` →
 * `previewSymmetricMargin` → a {@link MarginProposal} the card renders. The
 * shared gate (≤1-pending preamble, resolve-failure mapping, and the
 * adopt-or-release Apply/Reject wiring) lives in {@link mutatingToolRunner};
 * this file holds only what's specific to a symmetric margin — the input shape,
 * the grid-rounding, and the proposal's display fields. Pure/React-free.
 */
import {
    resolveSymmetricMargin,
    previewSymmetricMargin,
    commitSymmetricMargin,
    type SymmetricMarginDeps,
} from './pipeline';
import {
    asRecord,
    failedPlan,
    gateProposal,
    mutatingPreamble,
    normalizeDistanceInName,
    quantizeCmTracked,
    resolveFailureToPlan,
    type MutatingPlan,
    type MutatingToolCall,
    type MutationOutcome,
    type PendingFlag,
} from '../core';
import type { ApplySymmetricMarginInput } from '../../contract';

export type MarginToolCall = MutatingToolCall;

export interface MarginPlanDeps extends SymmetricMarginDeps, PendingFlag {}

export interface MarginProposal {
    readonly toolCallId: string;
    readonly target: {
        readonly roiNumber: number;
        readonly name: string;
        readonly currentVolumeCm3: number;
    };
    /** The canonical, grid-rounded margin actually previewed/applied. */
    readonly marginCm: number;
    /**
     * The clinician's ORIGINAL request, present only when it was finer than
     * the grid and therefore rounded. The card discloses the rounding.
     */
    readonly requestedMarginCm?: number;
    readonly newRoi: string;
    readonly volumeDeltaCm3: number;
    /** Commit the ALREADY-previewed geometry into a new structure. */
    apply(): Promise<MutationOutcome>;
    /** The clinician declined; the model is told and does not assume success. */
    reject(): MutationOutcome;
    /** Show a translucent overlay of the previewed geometry (card hover). */
    showPreview(): void;
    /** Clear the hover overlay. Never deletes the previewed geometry. */
    hidePreview(): void;
}

export type MarginPlan = MutatingPlan<MarginProposal>;

/** Defensively read the model-supplied (untyped) tool input. */
function readInput(input: unknown): ApplySymmetricMarginInput | undefined {
    const o = asRecord(input);
    if (o === undefined) {
        return undefined;
    }
    if (
        typeof o.roi !== 'string' ||
        typeof o.marginCm !== 'number' ||
        typeof o.newRoi !== 'string'
    ) {
        return undefined;
    }
    return { roi: o.roi, marginCm: o.marginCm, newRoi: o.newRoi };
}

export async function planMarginToolCall(
    call: MarginToolCall,
    deps: MarginPlanDeps
): Promise<MarginPlan> {
    const pre = mutatingPreamble(call, deps);
    if (pre !== 'ok') {
        return pre;
    }

    const parsed = readInput(call.input);
    if (parsed === undefined) {
        return failedPlan(
            call.toolCallId,
            'invalid apply_symmetric_margin input'
        );
    }

    // Round the request to the clinical grid (0.1 mm) — sub-grid precision is
    // below the achievable voxel resolution. The grid value is what gets
    // previewed, applied, and embedded in the new-structure name, so display,
    // geometry, and name cannot diverge. A finer original request is kept so
    // the card can disclose the rounding (CLAUDE.md: never silently clamp).
    const { cm: marginCm, requestedCm: requestedMarginCm } = quantizeCmTracked(
        parsed.marginCm
    );
    const newRoi = normalizeDistanceInName(parsed.newRoi, marginCm);

    const resolved = resolveSymmetricMargin(
        { roi: parsed.roi, marginCm, newRoi },
        deps
    );
    if (resolved.kind !== 'validated') {
        return resolveFailureToPlan(resolved, call.toolCallId);
    }

    const preview = await previewSymmetricMargin(resolved.validated, deps);
    if (preview.kind === 'failed') {
        return failedPlan(call.toolCallId, preview.reason);
    }

    const previewed = preview.previewed;
    const proposal: MarginProposal = {
        toolCallId: call.toolCallId,
        target: {
            roiNumber: resolved.validated.sourceRoiNumber,
            name: parsed.roi,
            currentVolumeCm3: resolved.validated.sourceVolumeCm3,
        },
        marginCm,
        ...(requestedMarginCm !== undefined ? { requestedMarginCm } : {}),
        newRoi,
        volumeDeltaCm3: previewed.volumeDeltaCm3,
        ...gateProposal(previewed.computed, deps, () =>
            commitSymmetricMargin(previewed, deps)
        ),
    };
    return { kind: 'proposal', toolCallId: call.toolCallId, proposal };
}
